import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { Calendar, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyLeave = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchApplications();
    fetchLeaveBalance();
  }, []);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/my-leave-applications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const year = new Date().getFullYear();
      const response = await fetch(
        `${API_URL}/api/leave-management/leave-balance/${user?.user_id}?year=${year}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setLeaveBalance(data);
      }
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const filteredApplications = applications.filter(
    (app) => filter === 'all' || app.status === filter
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Leave Applications</h1>
        <p className="text-gray-600 mt-1">View your leave balance and application history</p>
      </div>

      {/* Leave Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {leaveBalance.map((balance) => (
          <Card key={balance.id} className="bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-700">
                  {balance.leave_type_name}
                </div>
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: balance.leave_type_code === 'CL' ? '#4CAF50' : balance.leave_type_code === 'SL' ? '#FF9800' : '#2196F3' }}
                />
              </div>
              <div className="text-3xl font-bold text-blue-900 mb-2">
                {balance.current_balance}
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>Allocated:</span>
                  <span className="font-medium">{balance.allocated}</span>
                </div>
                <div className="flex justify-between">
                  <span>Used:</span>
                  <span className="font-medium">{balance.used}</span>
                </div>
                {balance.pending > 0 && (
                  <div className="flex justify-between text-yellow-700">
                    <span>Pending:</span>
                    <span className="font-medium">{balance.pending}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter Tabs */}
      <div className="mb-4 flex gap-2">
        {['all', 'pending', 'approved', 'rejected'].map((status) => (
          <Button
            key={status}
            onClick={() => setFilter(status)}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status !== 'all' && (
              <Badge variant="secondary" className="ml-2">
                {applications.filter((app) => app.status === status).length}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Applications List */}
      <Card>
        <CardHeader>
          <CardTitle>Leave Applications</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>No leave applications found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredApplications.map((application) => (
                <div
                  key={application.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">
                          {application.leave_type_name}
                        </h3>
                        {getStatusBadge(application.status)}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {application.is_paid ? '💰 Paid Leave' : '📝 Unpaid Leave'}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        {application.total_days}
                      </div>
                      <div className="text-xs text-gray-500">
                        day{application.total_days > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="text-xs text-gray-500">From Date</label>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Calendar className="h-4 w-4" />
                        {formatDate(application.from_date)}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">To Date</label>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Calendar className="h-4 w-4" />
                        {formatDate(application.to_date)}
                      </div>
                    </div>
                  </div>

                  {application.is_half_day && (
                    <div className="mb-3">
                      <Badge variant="outline">
                        {application.half_day_type === 'first_half'
                          ? 'First Half'
                          : 'Second Half'}
                      </Badge>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="text-xs text-gray-500">Reason</label>
                    <p className="text-sm text-gray-700">{application.reason}</p>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t">
                    <div>
                      Applied on:{' '}
                      {formatDate(application.applied_at)}
                    </div>
                    {application.approved_by && (
                      <div>
                        {application.status === 'approved' ? 'Approved' : 'Rejected'} by:{' '}
                        {application.approved_by}
                      </div>
                    )}
                  </div>

                  {application.rejection_reason && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm text-red-800">
                        <strong>Rejection Reason:</strong> {application.rejection_reason}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyLeave;